/**
 * Mock Organization Documents
 * Contains sample uploaded documents for organizations
 *
 * Each document has:
 * - Basic info (type, org, dates)
 * - File details (name, size, URL)
 * - Custom fields (extracted or manually entered)
 * - Status information
 * - Version history
 *
 * @mock This data will be replaced with API calls
 */

// Document statuses
export const documentStatuses = {
  ACTIVE: 'ACTIVE',
  EXPIRED: 'EXPIRED',
  EXPIRING_SOON: 'EXPIRING_SOON',
  PENDING_REVIEW: 'PENDING_REVIEW',
  REJECTED: 'REJECTED',
  ARCHIVED: 'ARCHIVED'
};

// Extraction statuses
export const extractionStatuses = {
  PENDING: 'PENDING',
  PROCESSING: 'PROCESSING',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
  MANUAL: 'MANUAL'
};

/**
 * Sample organizations for mock data
 */
export const mockOrganizations = [
  {
    id: 'org-001',
    name: 'ABC Trading LLC',
    tradeNameEn: 'ABC Trading',
    tradeNameAr: 'شركة أي بي سي للتجارة',
    licenseNumber: 'DED-123456',
    type: 'CUSTOMER',
    _isMock: true
  },
  {
    id: 'org-002',
    name: 'XYZ Consulting DMCC',
    tradeNameEn: 'XYZ Consulting',
    tradeNameAr: 'إكس واي زد للاستشارات',
    licenseNumber: 'DMCC-789012',
    type: 'CUSTOMER',
    _isMock: true
  }
];

/**
 * Mock uploaded documents
 */
export const mockDocuments = [
  // Trade License
  {
    id: 'doc-001',
    documentTypeId: 'dt-trade-license',
    documentTypeCode: 'TRADE_LICENSE',
    documentTypeName: 'Trade License',
    categoryId: 'cat-license',
    categoryCode: 'LICENSE',
    categoryName: 'Licenses & Permits',
    verticalId: 'v-uae-pro',
    organizationId: 'org-001',

    // File details
    fileName: 'ABC_Trading_Trade_License_2024.pdf',
    fileSize: 1245678,
    fileSizeFormatted: '1.2 MB',
    fileType: 'application/pdf',
    fileUrl: '/mock/documents/trade-license-001.pdf',
    thumbnailUrl: '/mock/thumbnails/trade-license-001.png',

    // Dates
    issueDate: '2024-01-15',
    expiryDate: '2025-01-14',
    uploadedAt: '2024-01-20T10:30:00Z',
    updatedAt: '2024-01-20T10:30:00Z',

    // Status
    status: documentStatuses.ACTIVE,
    daysUntilExpiry: 350,

    // Extraction
    extractionStatus: extractionStatuses.COMPLETED,
    extractedAt: '2024-01-20T10:31:00Z',
    extractionConfidence: 0.95,

    // Custom fields (extracted values)
    customFields: {
      licenseNumber: 'DED-123456',
      tradeNameEn: 'ABC Trading LLC',
      tradeNameAr: 'شركة أي بي سي للتجارة ذ.م.م',
      legalType: 'LLC',
      activities: 'General Trading, Import/Export',
      capital: '300000'
    },

    // Authority
    issuingAuthorityId: 'auth-ded',
    issuingAuthorityName: 'Department of Economic Development',
    issuingAuthorityShortName: 'DED',

    // Meta
    uploadedById: 'user-001',
    uploadedByName: 'Ahmed Khan',
    notes: 'Annual renewal completed',
    tags: ['primary', '2024'],
    version: 3,

    _isMock: true
  },

  // Establishment Card
  {
    id: 'doc-002',
    documentTypeId: 'dt-establishment-card',
    documentTypeCode: 'ESTABLISHMENT_CARD',
    documentTypeName: 'Establishment Card',
    categoryId: 'cat-gov-cards',
    categoryCode: 'GOV_CARD',
    categoryName: 'Government Cards',
    verticalId: 'v-uae-pro',
    organizationId: 'org-001',

    fileName: 'Establishment_Card_ABC.pdf',
    fileSize: 856432,
    fileSizeFormatted: '836 KB',
    fileType: 'application/pdf',
    fileUrl: '/mock/documents/establishment-card-001.pdf',
    thumbnailUrl: '/mock/thumbnails/establishment-card-001.png',

    issueDate: '2024-02-01',
    expiryDate: '2026-01-31',
    uploadedAt: '2024-02-05T14:20:00Z',
    updatedAt: '2024-02-05T14:20:00Z',

    status: documentStatuses.ACTIVE,
    daysUntilExpiry: 731,

    extractionStatus: extractionStatuses.COMPLETED,
    extractedAt: '2024-02-05T14:21:00Z',
    extractionConfidence: 0.92,

    customFields: {
      establishmentNumber: 'EST-2024-78901',
      companyName: 'ABC Trading LLC',
      establishmentType: 'Private Company',
      totalEmployees: '15',
      activityCode: 'TRADE-001'
    },

    issuingAuthorityId: 'auth-mohre',
    issuingAuthorityName: 'Ministry of Human Resources and Emiratization',
    issuingAuthorityShortName: 'MOHRE',

    uploadedById: 'user-001',
    uploadedByName: 'Ahmed Khan',
    notes: '',
    tags: ['government'],
    version: 1,

    _isMock: true
  },

  // Immigration Card
  {
    id: 'doc-003',
    documentTypeId: 'dt-immigration-card',
    documentTypeCode: 'IMMIGRATION_CARD',
    documentTypeName: 'Immigration Card',
    categoryId: 'cat-gov-cards',
    categoryCode: 'GOV_CARD',
    categoryName: 'Government Cards',
    verticalId: 'v-uae-pro',
    organizationId: 'org-001',

    fileName: 'Immigration_Card_ABC.pdf',
    fileSize: 745123,
    fileSizeFormatted: '728 KB',
    fileType: 'application/pdf',
    fileUrl: '/mock/documents/immigration-card-001.pdf',
    thumbnailUrl: '/mock/thumbnails/immigration-card-001.png',

    issueDate: '2023-06-15',
    expiryDate: '2025-06-14',
    uploadedAt: '2023-06-20T09:15:00Z',
    updatedAt: '2023-06-20T09:15:00Z',

    status: documentStatuses.ACTIVE,
    daysUntilExpiry: 500,

    extractionStatus: extractionStatuses.COMPLETED,
    extractedAt: '2023-06-20T09:16:00Z',
    extractionConfidence: 0.88,

    customFields: {
      immigrationFileNumber: 'ICA-2023-456789',
      companyName: 'ABC Trading LLC',
      validFrom: '2023-06-15',
      validUntil: '2025-06-14',
      sponsorName: 'ABC Trading LLC'
    },

    issuingAuthorityId: 'auth-ica',
    issuingAuthorityName: 'Federal Authority for Identity and Citizenship',
    issuingAuthorityShortName: 'ICA',

    uploadedById: 'user-002',
    uploadedByName: 'Sara Ali',
    notes: '',
    tags: [],
    version: 1,

    _isMock: true
  },

  // TRN Certificate (Tax)
  {
    id: 'doc-004',
    documentTypeId: 'dt-trn-certificate',
    documentTypeCode: 'TRN_CERTIFICATE',
    documentTypeName: 'Tax Registration Certificate',
    categoryId: 'cat-tax',
    categoryCode: 'TAX',
    categoryName: 'Tax & Compliance',
    verticalId: 'v-uae-pro',
    organizationId: 'org-001',

    fileName: 'TRN_Certificate_ABC.pdf',
    fileSize: 523456,
    fileSizeFormatted: '511 KB',
    fileType: 'application/pdf',
    fileUrl: '/mock/documents/trn-certificate-001.pdf',
    thumbnailUrl: '/mock/thumbnails/trn-certificate-001.png',

    issueDate: '2024-06-01',
    expiryDate: null, // TRN certificates don't expire
    uploadedAt: '2024-06-05T11:00:00Z',
    updatedAt: '2024-06-05T11:00:00Z',

    status: documentStatuses.ACTIVE,
    daysUntilExpiry: null,

    extractionStatus: extractionStatuses.COMPLETED,
    extractedAt: '2024-06-05T11:01:00Z',
    extractionConfidence: 0.97,

    customFields: {
      trn: '100123456789012',
      registeredName: 'ABC Trading LLC',
      registrationDate: '2024-06-01',
      entityType: 'Private Company Limited'
    },

    issuingAuthorityId: 'auth-fta',
    issuingAuthorityName: 'Federal Tax Authority',
    issuingAuthorityShortName: 'FTA',

    uploadedById: 'user-001',
    uploadedByName: 'Ahmed Khan',
    notes: 'Corporate Tax Registration',
    tags: ['tax', 'ct'],
    version: 1,

    _isMock: true
  },

  // MOA - Expiring Soon
  {
    id: 'doc-005',
    documentTypeId: 'dt-moa',
    documentTypeCode: 'MOA',
    documentTypeName: 'Memorandum of Association',
    categoryId: 'cat-formation',
    categoryCode: 'FORMATION',
    categoryName: 'Formation Documents',
    verticalId: 'v-uae-pro',
    organizationId: 'org-001',

    fileName: 'MOA_ABC_Trading.pdf',
    fileSize: 2345678,
    fileSizeFormatted: '2.2 MB',
    fileType: 'application/pdf',
    fileUrl: '/mock/documents/moa-001.pdf',
    thumbnailUrl: '/mock/thumbnails/moa-001.png',

    issueDate: '2019-03-15',
    expiryDate: '2025-03-14',
    uploadedAt: '2024-01-10T08:00:00Z',
    updatedAt: '2024-01-10T08:00:00Z',

    status: documentStatuses.EXPIRING_SOON,
    daysUntilExpiry: 45,

    extractionStatus: extractionStatuses.COMPLETED,
    extractedAt: '2024-01-10T08:02:00Z',
    extractionConfidence: 0.85,

    customFields: {
      companyName: 'ABC Trading LLC',
      registrationNumber: 'REG-2019-12345',
      shareCapital: '300000',
      shareholders: 'John Smith (51%), Mohammed Ali (49%)',
      establishmentDate: '2019-03-15'
    },

    issuingAuthorityId: 'auth-ded',
    issuingAuthorityName: 'Department of Economic Development',
    issuingAuthorityShortName: 'DED',

    uploadedById: 'user-001',
    uploadedByName: 'Ahmed Khan',
    notes: 'Original MOA - needs renewal soon',
    tags: ['formation', 'original'],
    version: 1,

    _isMock: true
  },

  // Office Tenancy Contract - Expired
  {
    id: 'doc-006',
    documentTypeId: 'dt-tenancy-contract',
    documentTypeCode: 'TENANCY_CONTRACT',
    documentTypeName: 'Office Tenancy Contract',
    categoryId: 'cat-operational',
    categoryCode: 'OPERATIONAL',
    categoryName: 'Operational Documents',
    verticalId: 'v-uae-pro',
    organizationId: 'org-001',

    fileName: 'Tenancy_Contract_2023.pdf',
    fileSize: 1567890,
    fileSizeFormatted: '1.5 MB',
    fileType: 'application/pdf',
    fileUrl: '/mock/documents/tenancy-001.pdf',
    thumbnailUrl: '/mock/thumbnails/tenancy-001.png',

    issueDate: '2023-01-01',
    expiryDate: '2024-12-31',
    uploadedAt: '2023-01-05T10:00:00Z',
    updatedAt: '2023-01-05T10:00:00Z',

    status: documentStatuses.EXPIRED,
    daysUntilExpiry: -30,

    extractionStatus: extractionStatuses.COMPLETED,
    extractedAt: '2023-01-05T10:01:00Z',
    extractionConfidence: 0.90,

    customFields: {
      propertyAddress: 'Office 501, Business Bay Tower, Dubai',
      landlordName: 'Dubai Properties LLC',
      ejariNumber: 'EJARI-2023-789456',
      annualRent: '150000',
      paymentTerms: 'Quarterly'
    },

    issuingAuthorityId: 'auth-rera',
    issuingAuthorityName: 'Real Estate Regulatory Authority',
    issuingAuthorityShortName: 'RERA',

    uploadedById: 'user-002',
    uploadedByName: 'Sara Ali',
    notes: 'EXPIRED - New contract pending',
    tags: ['expired', 'office'],
    version: 2,

    _isMock: true
  },

  // VAT Certificate
  {
    id: 'doc-007',
    documentTypeId: 'dt-vat-certificate',
    documentTypeCode: 'VAT_CERTIFICATE',
    documentTypeName: 'VAT Registration Certificate',
    categoryId: 'cat-tax',
    categoryCode: 'TAX',
    categoryName: 'Tax & Compliance',
    verticalId: 'v-uae-pro',
    organizationId: 'org-001',

    fileName: 'VAT_Certificate_ABC.pdf',
    fileSize: 456789,
    fileSizeFormatted: '446 KB',
    fileType: 'application/pdf',
    fileUrl: '/mock/documents/vat-certificate-001.pdf',
    thumbnailUrl: '/mock/thumbnails/vat-certificate-001.png',

    issueDate: '2018-01-01',
    expiryDate: null,
    uploadedAt: '2024-01-15T09:30:00Z',
    updatedAt: '2024-01-15T09:30:00Z',

    status: documentStatuses.ACTIVE,
    daysUntilExpiry: null,

    extractionStatus: extractionStatuses.COMPLETED,
    extractedAt: '2024-01-15T09:31:00Z',
    extractionConfidence: 0.96,

    customFields: {
      vatTrn: '100123456789012',
      registeredName: 'ABC Trading LLC',
      effectiveDate: '2018-01-01',
      registrationCategory: 'Mandatory'
    },

    issuingAuthorityId: 'auth-fta',
    issuingAuthorityName: 'Federal Tax Authority',
    issuingAuthorityShortName: 'FTA',

    uploadedById: 'user-001',
    uploadedByName: 'Ahmed Khan',
    notes: '',
    tags: ['tax', 'vat'],
    version: 1,

    _isMock: true
  },

  // CT Return - Pending Review
  {
    id: 'doc-008',
    documentTypeId: 'dt-ct-return',
    documentTypeCode: 'CT_RETURN',
    documentTypeName: 'Corporate Tax Return',
    categoryId: 'cat-tax',
    categoryCode: 'TAX',
    categoryName: 'Tax & Compliance',
    verticalId: 'v-uae-pro',
    organizationId: 'org-001',

    fileName: 'CT_Return_2024_ABC.pdf',
    fileSize: 987654,
    fileSizeFormatted: '964 KB',
    fileType: 'application/pdf',
    fileUrl: '/mock/documents/ct-return-001.pdf',
    thumbnailUrl: '/mock/thumbnails/ct-return-001.png',

    issueDate: '2024-09-30',
    expiryDate: '2025-09-29',
    uploadedAt: '2024-10-01T16:00:00Z',
    updatedAt: '2024-10-01T16:00:00Z',

    status: documentStatuses.PENDING_REVIEW,
    daysUntilExpiry: 365,

    extractionStatus: extractionStatuses.PROCESSING,
    extractedAt: null,
    extractionConfidence: null,

    customFields: {
      taxPeriodStart: '2024-01-01',
      taxPeriodEnd: '2024-12-31',
      revenue: null,
      taxableIncome: null,
      taxPayable: null,
      filingDate: '2024-09-30'
    },

    issuingAuthorityId: 'auth-fta',
    issuingAuthorityName: 'Federal Tax Authority',
    issuingAuthorityShortName: 'FTA',

    uploadedById: 'user-001',
    uploadedByName: 'Ahmed Khan',
    notes: 'Extraction in progress',
    tags: ['tax', 'ct', '2024'],
    version: 1,

    _isMock: true
  }
];

/**
 * Document version history mock
 */
export const mockDocumentVersions = {
  'doc-001': [
    {
      version: 3,
      fileName: 'ABC_Trading_Trade_License_2024.pdf',
      uploadedAt: '2024-01-20T10:30:00Z',
      uploadedByName: 'Ahmed Khan',
      notes: 'Annual renewal completed',
      isCurrent: true,
      _isMock: true
    },
    {
      version: 2,
      fileName: 'ABC_Trading_Trade_License_2023.pdf',
      uploadedAt: '2023-01-18T09:00:00Z',
      uploadedByName: 'Ahmed Khan',
      notes: '2023 renewal',
      isCurrent: false,
      _isMock: true
    },
    {
      version: 1,
      fileName: 'ABC_Trading_Trade_License_2022.pdf',
      uploadedAt: '2022-01-15T11:00:00Z',
      uploadedByName: 'Sara Ali',
      notes: 'Initial upload',
      isCurrent: false,
      _isMock: true
    }
  ],
  'doc-006': [
    {
      version: 2,
      fileName: 'Tenancy_Contract_2023.pdf',
      uploadedAt: '2023-01-05T10:00:00Z',
      uploadedByName: 'Sara Ali',
      notes: 'Renewed contract',
      isCurrent: true,
      _isMock: true
    },
    {
      version: 1,
      fileName: 'Tenancy_Contract_2022.pdf',
      uploadedAt: '2022-01-10T14:00:00Z',
      uploadedByName: 'Ahmed Khan',
      notes: 'Initial contract',
      isCurrent: false,
      _isMock: true
    }
  ]
};

/**
 * Get documents by organization
 */
export const getDocumentsByOrganization = (orgId) => {
  return mockDocuments.filter(doc => doc.organizationId === orgId);
};

/**
 * Get documents by category
 */
export const getDocumentsByCategory = (orgId, categoryCode) => {
  return mockDocuments.filter(
    doc => doc.organizationId === orgId && doc.categoryCode === categoryCode
  );
};

/**
 * Get document summary counts
 */
export const getDocumentSummary = (orgId) => {
  const docs = getDocumentsByOrganization(orgId);
  return {
    total: docs.length,
    active: docs.filter(d => d.status === documentStatuses.ACTIVE).length,
    expiringSoon: docs.filter(d => d.status === documentStatuses.EXPIRING_SOON).length,
    expired: docs.filter(d => d.status === documentStatuses.EXPIRED).length,
    pendingReview: docs.filter(d => d.status === documentStatuses.PENDING_REVIEW).length,
    byCategory: {
      LICENSE: docs.filter(d => d.categoryCode === 'LICENSE').length,
      GOV_CARD: docs.filter(d => d.categoryCode === 'GOV_CARD').length,
      FORMATION: docs.filter(d => d.categoryCode === 'FORMATION').length,
      OPERATIONAL: docs.filter(d => d.categoryCode === 'OPERATIONAL').length,
      TAX: docs.filter(d => d.categoryCode === 'TAX').length
    },
    _isMock: true
  };
};

/**
 * Get document versions
 */
export const getDocumentVersions = (documentId) => {
  return mockDocumentVersions[documentId] || [];
};

export default mockDocuments;
